import {
  APPLICATION_REPLY_DAYS,
  adminApplicationSchema,
  padrinhoSummarySchema,
  type PadrinhoSummary,
  type AdminApplication,
  type ApplicationStatus,
} from '@growup/streaming-shared';
import { Button, Card, Field, Input, Notice, Select, StatusBadge } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { APPLICATION_LOOK, LEVEL_LABEL } from './labels';
import { padrinhoOption } from './PadrinhosView';

type Filter = ApplicationStatus | 'all';

/**
 * Streamer applications: approve (becomes an active streamer) or reject, with
 * a note. Pending ones show how long they have waited, in red past the
 * deadline (GS18). `onChanged` refreshes the counter on top.
 */
export function StreamerApplications({ onChanged }: { onChanged?: () => void } = {}) {
  const [status, setStatus] = useState<Filter>('pending');
  const [list, setList] = useState<AdminApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [padrinhos, setPadrinhos] = useState<PadrinhoSummary[]>([]);

  // Os padrinhos com lugar, para o provisorio ao aprovar (GS1). Sem eles, aprova-se sem.
  useEffect(() => {
    getJson('/api/streaming/padrinhos', z.array(padrinhoSummarySchema))
      .then(setPadrinhos)
      .catch(() => setPadrinhos([]));
  }, [list]);

  const load = useCallback(() => {
    setError(null);
    const query = status === 'all' ? '' : `?status=${status}`;
    getJson(`/api/streaming/applications${query}`, z.array(adminApplicationSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [status]);
  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Estado" className="w-48">
          <Select value={status} onChange={(e) => setStatus(e.target.value as Filter)}>
            {(Object.keys(APPLICATION_LOOK) as ApplicationStatus[]).map((s) => (
              <option key={s} value={s}>
                {APPLICATION_LOOK[s].label}
              </option>
            ))}
            <option value="all">Todas</option>
          </Select>
        </Field>
        <Button variant="secondary" size="sm" className="mb-1.5" onClick={load}>
          Atualizar
        </Button>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {list && list.length === 0 ? (
        <p className="text-gu-ink-muted">
          {status === 'pending' ? 'Não há candidaturas à espera.' : 'Nenhuma candidatura.'}
        </p>
      ) : null}
      {list?.map((a) => (
        <ApplicationCard
          key={a.id}
          application={a}
          padrinhos={padrinhos}
          onDecided={() => {
            load();
            onChanged?.();
          }}
        />
      ))}
    </div>
  );
}

function ApplicationCard({
  application: a,
  padrinhos,
  onDecided,
}: {
  application: AdminApplication;
  padrinhos: PadrinhoSummary[];
  onDecided: () => void;
}) {
  const [note, setNote] = useState('');
  const [padrinhoId, setPadrinhoId] = useState('');
  const available = padrinhos.filter((p) => p.free > 0 && p.accountId !== a.accountId);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const decide = async (decision: 'approve' | 'reject') => {
    setError(null);
    setBusy(true);
    try {
      await send('POST', `/api/streaming/applications/${a.id}/decision`, {
        decision,
        note: note.trim() || null,
        ...(decision === 'approve' && padrinhoId !== '' ? { padrinhoId } : {}),
      });
      onDecided();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      headingLevel={2}
      eyebrow={
        <>
          Candidatura de {formatDateTime(a.createdAt)}
          {a.waitingDays !== null ? (
            <span className={a.overdue ? 'font-semibold text-gu-danger' : undefined}>
              {' '}
              · {waitingLabel(a.waitingDays)}
              {a.overdue ? ` · fora do prazo de ${String(APPLICATION_REPLY_DAYS)} dias` : ''}
            </span>
          ) : null}
        </>
      }
      title={
        <a
          className="gu-link"
          href={`https://www.twitch.tv/${a.channel}`}
          target="_blank"
          rel="noreferrer"
        >
          twitch.tv/{a.channel}
        </a>
      }
      actions={
        <StatusBadge tone={APPLICATION_LOOK[a.status].tone}>
          {APPLICATION_LOOK[a.status].label}
        </StatusBadge>
      }
    >
      <p className="break-words text-gu-ink-muted">
        Conta {a.login}
        {a.email ? ` · ${a.email}` : ''}
        {a.level ? ` · ${LEVEL_LABEL[a.level] ?? a.level}` : ''} ·{' '}
        {a.twitchVerified
          ? 'canal verificado (ligado à conta)'
          : 'canal escrito à mão, por verificar'}
      </p>
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <dt className="text-gu-ink-muted">O que transmite</dt>
        <dd className="whitespace-pre-line">{a.content}</dd>
        <dt className="text-gu-ink-muted">Quando</dt>
        <dd className="whitespace-pre-line">{a.schedule}</dd>
        <dt className="text-gu-ink-muted">Porque quer</dt>
        <dd className="whitespace-pre-line">{a.motivation}</dd>
      </dl>
      {a.decidedAt ? (
        <p className="text-gu-ink-muted">
          Decidida por {a.decidedBy ?? '?'} a {formatDateTime(a.decidedAt)}
          {a.decisionNote ? ` · Nota: ${a.decisionNote}` : ''}
        </p>
      ) : null}
      {a.status === 'pending' ? (
        <>
          <Field label="Nota para o candidato" hint="Vai no email da decisão." optional>
            <Input value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Field
            label="Padrinho provisório"
            hint="Só os que têm lugares livres. Confirma-se ou troca-se na reunião mensal."
            optional
            className="max-w-sm"
          >
            <Select value={padrinhoId} onChange={(e) => setPadrinhoId(e.target.value)}>
              <option value="">Escolher depois</option>
              {available.map((p) => (
                <option key={p.accountId} value={p.accountId}>
                  {padrinhoOption(p)}
                </option>
              ))}
            </Select>
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <div className="flex gap-3">
            <Button size="sm" disabled={busy} onClick={() => void decide('approve')}>
              ✓ Aprovar
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={() => void decide('reject')}
            >
              ✕ Recusar
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function waitingLabel(days: number): string {
  if (days === 0) return 'chegou hoje';
  return days === 1 ? 'espera há 1 dia' : `espera há ${String(days)} dias`;
}
